import { useQuery } from '@tanstack/react-query';

import { fetchOngoingEvent } from './api';
import type { OngoingEventComputed } from './api';

function getHttpStatus(err: unknown): number | null {
  const e: any = err;
  return e?.response?.status ?? e?.request?.status ?? null;
}

export const videoQueryKeys = {
  ongoingEvent: (scheduleId: number | string) =>
    ['video', 'ongoingEvent', scheduleId] as const,
};

export function useOngoingEventQuery(params: {
  scheduleId: number | string | null | undefined;
  enabled?: boolean;
}) {
  const { scheduleId, enabled } = params;
  const isEnabled = enabled ?? scheduleId != null;

  return useQuery<OngoingEventComputed>({
    queryKey: videoQueryKeys.ongoingEvent(scheduleId ?? 'none'),
    queryFn: () => {
      if (scheduleId == null) return Promise.resolve({} as OngoingEventComputed);
      return fetchOngoingEvent({ schedule_id: scheduleId });
    },
    enabled: isEnabled && scheduleId != null,
    staleTime: 5_000,
    refetchInterval: (ctx) => {
      if (ctx.state.error) return false;
      if (ctx.state.fetchStatus === 'fetching') return 1_000;
      if (ctx.state.data?.event_id != null) return 10_000;
      return 1_000;
    },
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      const status = getHttpStatus(error);
      if (status === 404) return false; // no ongoing event or route not found; do not retry
      return failureCount < 3;
    },
    retryDelay: (attemptIndex, error) => {
      const status = getHttpStatus(error);
      if (status === 404) return 0;
      return Math.min(1000 * 2 ** attemptIndex, 10_000);
    },
  });
}

